import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import Select from 'react-select'
import { ImCross, ImPencil } from 'react-icons/im'
import { FaBoxesStacked, FaList, FaMagnifyingGlass, FaMobileScreenButton, FaTableCellsLarge, FaTag } from 'react-icons/fa6'
import { toast } from 'react-toastify'

import '../Dashboard.css'
import '../PhonesStock.css'
import Header from '../components/Header'
import Footer from '../components/Footer'
import ScrollToTop from '../components/ScrollTop'
import PreloadFetch from '../components/PreloadFetch'
import { useAuth } from '../provider/authProvider'

moment.locale('es')

const BRAND_OPTIONS = [
    'HONOR', 'XIAOMI', 'REDMI', 'POCO', 'SAMSUNG', 'IPHONE', 'MOTOROLA', 'ZTE',
    'NUBIA', 'TECNO', 'MEIZU', 'INFINIX', 'REALME', 'OPPO', 'VIVO', 'ITEL'
]

const BRAND_THEME = {
    HONOR: { primary: '#00AEEF', secondary: '#0B58D0', soft: '#EAF8FF', softTwo: '#F6FCFF', text: '#063B68' },
    XIAOMI: { primary: '#FF6900', secondary: '#F9A01B', soft: '#FFF3E8', softTwo: '#FFF9F2', text: '#663000' },
    REDMI: { primary: '#E53935', secondary: '#FF7043', soft: '#FFF0EF', softTwo: '#FFF8F6', text: '#6D1210' },
    POCO: { primary: '#FFD400', secondary: '#111111', soft: '#FFF8CC', softTwo: '#FFFEF2', text: '#272100' },
    SAMSUNG: { primary: '#1428A0', secondary: '#00A9E0', soft: '#EEF2FF', softTwo: '#F7FAFF', text: '#07135D' },
    IPHONE: { primary: '#1D1D1F', secondary: '#86868B', soft: '#F2F2F4', softTwo: '#FBFBFC', text: '#1D1D1F' },
    MOTOROLA: { primary: '#0057B8', secondary: '#00AEEF', soft: '#EAF4FF', softTwo: '#F6FBFF', text: '#063B75' },
    ZTE: { primary: '#0072CE', secondary: '#00B5E2', soft: '#E9F6FF', softTwo: '#F7FCFF', text: '#06446F' },
    NUBIA: { primary: '#E60012', secondary: '#111111', soft: '#FFECEE', softTwo: '#FFF7F8', text: '#6E0009' },
    TECNO: { primary: '#0077C8', secondary: '#69BE28', soft: '#ECF8FF', softTwo: '#F7FFF2', text: '#064366' },
    MEIZU: { primary: '#00A7EA', secondary: '#2B5EFF', soft: '#EAF8FF', softTwo: '#F4F7FF', text: '#063F6B' },
    INFINIX: { primary: '#00C853', secondary: '#111111', soft: '#E9FFF2', softTwo: '#F8FFF9', text: '#075128' },
    REALME: { primary: '#FFC915', secondary: '#111111', soft: '#FFF7D8', softTwo: '#FFFDF2', text: '#5A4700' },
    OPPO: { primary: '#046A38', secondary: '#0BBF6A', soft: '#EAF8F0', softTwo: '#F6FFF9', text: '#073A21' },
    VIVO: { primary: '#415FFF', secondary: '#00B3FF', soft: '#EEF2FF', softTwo: '#F6FBFF', text: '#13206B' }
}

const getBrandThemeStyle = (brand) => {
    const theme = BRAND_THEME[brand] || { primary: '#1f7ae0', secondary: '#0f54b8', soft: '#eef6ff', softTwo: '#ffffff', text: '#10233f' }

    return {
        '--brand-primary': theme.primary,
        '--brand-secondary': theme.secondary,
        '--brand-soft': theme.soft,
        '--brand-soft-two': theme.softTwo,
        '--brand-text': theme.text
    }
}

const RAM_OPTIONS = ['2GB', '3GB', '4GB', '6GB', '8GB', '12GB', '16GB'].map((item) => ({ value: item, label: item }))
const ROM_OPTIONS = ['32GB', '64GB', '128GB', '256GB', '512GB', '1024GB'].map((item) => ({ value: item, label: item }))
const BRAND_SELECT_OPTIONS = BRAND_OPTIONS.map((item) => ({ value: item, label: item }))

const ORDER_OPTIONS = [
    { value: 'default', label: 'Orden original' },
    { value: 'price_asc', label: 'Precio: menor a mayor' },
    { value: 'price_desc', label: 'Precio: mayor a menor' }
]

const PRICE_TYPE_OPTIONS = [
    { value: 'all', label: 'Todos los precios' },
    { value: 'normal', label: 'Precio normal' },
    { value: 'promo', label: 'Promociones / contado' },
    { value: 'drop', label: 'Bajaron de precio' }
]

const NEW_ENTRY_OPTIONS = [
    { value: 'all', label: 'Todos los ingresos' },
    { value: 'new', label: 'Nuevos ingresos' }
]

const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'active', label: 'Activos' },
    { value: 'inactive', label: 'Inactivos' }
]

const initialForm = {
    marca: null,
    modelo: '',
    codigo: '',
    ram: null,
    rom: null,
    price: '',
    estado: '1'
}

const selectStyles = {
    control: (base, state) => ({
        ...base,
        height: 44,
        minHeight: 44,
        borderRadius: 12,
        borderColor: state.isFocused ? '#1f7ae0' : '#dde7f3',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(31, 122, 224, .12)' : 'none',
        '&:hover': { borderColor: '#1f7ae0' }
    }),
    valueContainer: (base) => ({
        ...base,
        height: 44,
        padding: '0 10px'
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: 44
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: 12
    })
}

const money = (value) => new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
}).format(Number(value || 0))

const getUserRole = (authData) => String(
    authData?.rol || authData?.role || localStorage.getItem('rol') || localStorage.getItem('role') || ''
)

const getComputedPrices = (phone) => {
    const basePrice = Number(phone?.precio || 0)

    return {
        basePrice,
        normalPrice: basePrice * 1.2,
        cashPrice: Math.max(basePrice - 10, 0)
    }
}

const isNewPhone = (phone) => {
    if (Number(phone?.nuevoIngreso) === 1) return true
    if (!phone?.fechaIngreso) return false
    return moment().diff(moment(phone.fechaIngreso), 'days') <= 30
}

const isPriceDrop = (phone) => {
    if (Number(phone?.bajoPrecio) === 1) return true

    const previous = Number(phone?.precioAnterior || 0)
    const current = Number(phone?.precio || 0)
    const recent = phone?.fechaCambioPrecio
        ? moment().diff(moment(phone.fechaCambioPrecio), 'days') <= 30
        : false

    return previous > current && recent
}

function Phones(props) {
    const auth = useAuth()
    const { user, lectura } = auth
    const role = getUserRole(auth)

    const canView = ['1', '3', '5', '7', '8', '9'].includes(role)
    const canAdd = role === '1'
    const canEdit = role === '1'

    const [listPhones, setListPhones] = useState([])
    const [preload, setPreload] = useState(false)
    const [showPopupAddStock, setShowPopupAddStock] = useState(false)
    const [showPopupEditStock, setShowPopupEditStock] = useState(false)
    const [editingPhone, setEditingPhone] = useState(null)
    const [form, setForm] = useState(initialForm)
    const [errors, setErrors] = useState({})

    const [search, setSearch] = useState('')
    const [brandFilter, setBrandFilter] = useState([])
    const [ramFilter, setRamFilter] = useState([])
    const [romFilter, setRomFilter] = useState([])
    const [orderFilter, setOrderFilter] = useState(ORDER_OPTIONS[0])
    const [priceTypeFilter, setPriceTypeFilter] = useState(PRICE_TYPE_OPTIONS[0])
    const [newEntryFilter, setNewEntryFilter] = useState(NEW_ENTRY_OPTIONS[0])
    const [priceMin, setPriceMin] = useState('')
    const [priceMax, setPriceMax] = useState('')
    const [layoutMode, setLayoutMode] = useState('compact')
    const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_OPTIONS[1])
    const [selectedPhones, setSelectedPhones] = useState([])

    const getPhones = async () => {
        try {
            setPreload(true)

            const response = await axios({
                method: 'get',
                url: `${process.env.REACT_APP_API}/phones`,
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            })

            const resData = response.data

            if (resData.msg === 'errorRefresh') {
                localStorage.removeItem('token')
                localStorage.removeItem('name')
                localStorage.removeItem('user')
                window.location.reload()
                return
            }

            if (resData.msg === 'forbidden') {
                setListPhones([])
                toast.error('No tienes permisos para visualizar stock.')
                return
            }

            setListPhones(Array.isArray(resData.data) ? resData.data : [])
        } catch (err) {
            console.error(err)
            toast.error('No se pudo cargar el stock.')
        } finally {
            setPreload(false)
        }
    }

    const resetFilters = () => {
        setSearch('')
        setBrandFilter([])
        setRamFilter([])
        setRomFilter([])
        setOrderFilter(ORDER_OPTIONS[0])
        setPriceTypeFilter(PRICE_TYPE_OPTIONS[0])
        setNewEntryFilter(NEW_ENTRY_OPTIONS[0])
        setPriceMin('')
        setPriceMax('')
        setStatusFilter(STATUS_FILTER_OPTIONS[1])
    }

    const openAddModal = () => {
        setForm(initialForm)
        setErrors({})
        setShowPopupAddStock(true)
    }

    const openEditModal = (phone) => {
        setEditingPhone(phone)
        setForm({
            marca: { value: phone.marca, label: phone.marca },
            modelo: phone.modelo || '',
            codigo: phone.code || '',
            ram: { value: phone.ram, label: phone.ram },
            rom: { value: phone.rom, label: phone.rom },
            price: String(phone.precio || ''),
            estado: String(phone.estado) === '0' ? '0' : '1'
        })
        setErrors({})
        setShowPopupEditStock(true)
    }

    const closeModals = () => {
        setShowPopupAddStock(false)
        setShowPopupEditStock(false)
        setEditingPhone(null)
        setForm(initialForm)
        setErrors({})
    }

    const updateForm = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    const validateForm = () => {
        const nextErrors = {}
        const price = Number(form.price)

        if (!form.marca?.value) nextErrors.marca = 'Selecciona una marca.'
        if (!form.modelo.trim() || form.modelo.trim().length < 2) nextErrors.modelo = 'Ingresa un modelo válido.'
        if (!form.ram?.value) nextErrors.ram = 'Selecciona la RAM.'
        if (!form.rom?.value) nextErrors.rom = 'Selecciona la ROM.'
        if (!form.price.trim()) nextErrors.price = 'Ingresa el precio.'

        if (form.price.trim() && (!/^\d+(\.\d{1,2})?$/.test(form.price) || price <= 0)) {
            nextErrors.price = 'El precio debe ser mayor a 0.'
        }

        setErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
    }

    const saveStock = async (e, status) => {
        e.preventDefault()
        if (!validateForm()) return

        try {
            setPreload(true)

            const objPhone = {
                marca: form.marca.value,
                modelo: form.modelo,
                codigo: form.codigo,
                ram: form.ram.value,
                rom: form.rom.value,
                price: form.price,
                estado: status === 'edit' ? form.estado : '1'
            }

            const pathApi = status === 'add'
                ? `${process.env.REACT_APP_API}/add-phone`
                : `${process.env.REACT_APP_API}/edit-phone`

            const response = await axios({
                method: 'POST',
                url: pathApi,
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    linkNumber: status === 'edit' ? editingPhone?.linkNumber : null,
                    objPhone
                }
            })

            const resData = response.data

            if (resData.msg === 'success') {
                setListPhones(Array.isArray(resData.data) ? resData.data : [])
                toast.success(status === 'add' ? 'Equipo agregado con éxito.' : 'Equipo editado con éxito.')
                closeModals()
                return
            }

            if (resData.msg === 'forbiddenAdd') {
                toast.error('Solo el rol 1 puede agregar equipos.')
                return
            }

            if (resData.msg === 'forbiddenEdit') {
                toast.error('Solo el rol 1 puede editar equipos.')
                return
            }

            if (resData.msg === 'validation') {
                toast.error(resData.errors?.[0] || 'Valida los datos ingresados.')
                return
            }

            toast.error('No se pudo guardar el equipo.')
        } catch (err) {
            console.error(err)
            toast.error(err?.response?.data?.errors?.[0] || 'No se pudo guardar el equipo.')
        } finally {
            setPreload(false)
        }
    }

    const filteredPhones = useMemo(() => {
        const selectedBrands = brandFilter.map((item) => item.value)
        const selectedRam = ramFilter.map((item) => item.value)
        const selectedRom = romFilter.map((item) => item.value)
        const query = search.trim().toUpperCase()
        const min = priceMin !== '' ? Number(priceMin) : null
        const max = priceMax !== '' ? Number(priceMax) : null

        let data = [...listPhones].filter((phone) => {
            const { basePrice, normalPrice, cashPrice } = getComputedPrices(phone)
            const searchable = `${phone.marca || ''} ${phone.modelo || ''}`.toUpperCase()

            if (query && !searchable.includes(query)) return false
            if (selectedBrands.length > 0 && !selectedBrands.includes(phone.marca)) return false
            if (selectedRam.length > 0 && !selectedRam.includes(phone.ram)) return false
            if (selectedRom.length > 0 && !selectedRom.includes(phone.rom)) return false

            const priceForRange = priceTypeFilter.value === 'normal'
                ? normalPrice
                : priceTypeFilter.value === 'promo'
                    ? cashPrice
                    : basePrice

            if (min !== null && priceForRange < min) return false
            if (max !== null && priceForRange > max) return false
            if (priceTypeFilter.value === 'drop' && !isPriceDrop(phone)) return false
            if (newEntryFilter.value === 'new' && !isNewPhone(phone)) return false
            if (statusFilter.value === 'active' && String(phone.estado) !== '1') return false
            if (statusFilter.value === 'inactive' && String(phone.estado) !== '0') return false

            return true
        })

        if (orderFilter.value === 'price_asc') {
            data.sort((a, b) => Number(a.precio || 0) - Number(b.precio || 0))
        }

        if (orderFilter.value === 'price_desc') {
            data.sort((a, b) => Number(b.precio || 0) - Number(a.precio || 0))
        }

        return data
    }, [
        listPhones,
        search,
        brandFilter,
        ramFilter,
        romFilter,
        orderFilter,
        priceTypeFilter,
        newEntryFilter,
        statusFilter,
        priceMin,
        priceMax
    ])

    const groupedPhones = useMemo(() => {
        return BRAND_OPTIONS.map((brand) => ({
            brand,
            items: filteredPhones.filter((phone) => phone.marca === brand)
        })).filter((group) => group.items.length > 0)
    }, [filteredPhones])

    const summary = useMemo(() => ({
        total: filteredPhones.length,
        nuevos: filteredPhones.filter(isNewPhone).length,
        bajaron: filteredPhones.filter(isPriceDrop).length,
        inactivos: filteredPhones.filter((phone) => String(phone.estado) === '0').length,
        marcas: groupedPhones.length
    }), [filteredPhones, groupedPhones])



    const selectedPhonesSet = useMemo(() => new Set(selectedPhones), [selectedPhones])

    const selectablePhones = useMemo(() => {
        return filteredPhones.filter((phone) => String(phone.estado) === '1')
    }, [filteredPhones])

    const togglePhoneSelection = (phoneId) => {
        setSelectedPhones((prev) => {
            const phoneIdString = String(phoneId)
            if (prev.includes(phoneIdString)) {
                return prev.filter((item) => item !== phoneIdString)
            }
            return [...prev, phoneIdString]
        })
    }

    const toggleGroupSelection = (items = []) => {
        const activeItems = items.filter((phone) => String(phone.estado) === '1')
        const activeIds = activeItems.map((phone) => String(phone.linkNumber))
        const allSelected = activeIds.length > 0 && activeIds.every((id) => selectedPhonesSet.has(id))

        setSelectedPhones((prev) => {
            if (allSelected) {
                return prev.filter((id) => !activeIds.includes(id))
            }

            return Array.from(new Set([...prev, ...activeIds]))
        })
    }

    const selectAllVisiblePhones = () => {
        const ids = selectablePhones.map((phone) => String(phone.linkNumber))
        setSelectedPhones(ids)
    }

    const clearSelectedPhones = () => {
        setSelectedPhones([])
    }

    const bulkDeactivatePhones = async () => {
        if (!canEdit || lectura === 1) return

        if (selectedPhones.length === 0) {
            toast.info('Selecciona al menos un equipo activo para desactivar.')
            return
        }

        const confirmMessage = `Se desactivarán ${selectedPhones.length} equipo(s). Solo el rol 1 podrá verlos después. ¿Deseas continuar?`
        if (!window.confirm(confirmMessage)) return

        try {
            setPreload(true)

            const response = await axios({
                method: 'POST',
                url: `${process.env.REACT_APP_API}/bulk-deactivate-phones`,
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    phoneIds: selectedPhones
                }
            })

            const resData = response.data

            if (resData.msg === 'success') {
                setListPhones(Array.isArray(resData.data) ? resData.data : [])
                setSelectedPhones([])
                toast.success(`Se desactivaron ${resData.updated || selectedPhones.length} equipo(s) correctamente.`)
                return
            }

            if (resData.msg === 'forbiddenEdit') {
                toast.error('Solo el rol 1 puede desactivar equipos en masa.')
                return
            }

            if (resData.msg === 'validation') {
                toast.error(resData.errors?.[0] || 'Selecciona equipos válidos.')
                return
            }

            toast.error('No se pudieron desactivar los equipos seleccionados.')
        } catch (err) {
            console.error(err)
            toast.error(err?.response?.data?.errors?.[0] || 'No se pudieron desactivar los equipos seleccionados.')
        } finally {
            setPreload(false)
        }
    }

    useEffect(() => {
        if (canView) getPhones()
    }, [canView])

    useEffect(() => {
        setSelectedPhones((prev) => prev.filter((id) => selectablePhones.some((phone) => String(phone.linkNumber) === id)))
    }, [selectablePhones])

    if (!canView) {
        return (
            <div>
                <ScrollToTop />
                <Header user={user} namePage={props.namePage} />
                <section className="sectionBody sectionBodyPhonesPro">
                    <div className="containerCustom containerBody">
                        <div className="stockAccessDenied">
                            <h2>Acceso restringido</h2>
                            <p>Esta página solo puede ser visualizada por usuarios con rol 1, 3, 7 y 8.</p>
                        </div>
                    </div>
                </section>
                <Footer />
            </div>
        )
    }

    const renderModal = (type) => {
        const isEdit = type === 'edit'

        return (
            <div className="stockModalOverlay">
                <div className="stockModal">
                    <button type="button" onClick={closeModals} className="stockModalClose">
                        <ImCross />
                    </button>

                    <div className="stockModalHeader">
                        <span>{isEdit ? 'Editar equipo' : 'Nuevo equipo'}</span>
                        <h3>{isEdit ? 'EDITAR STOCK' : 'AGREGAR STOCK'}</h3>
                        <p>Completa la información del modelo con datos claros y validados.</p>
                    </div>

                    <form className="stockForm" onSubmit={(e) => saveStock(e, isEdit ? 'edit' : 'add')}>
                        <div className="stockFormGroup">
                            <label>Marca</label>
                            <Select
                                value={form.marca}
                                onChange={(value) => updateForm('marca', value)}
                                options={BRAND_SELECT_OPTIONS}
                                styles={selectStyles}
                                placeholder="Selecciona marca"
                            />
                            {errors.marca && <small>{errors.marca}</small>}
                        </div>

                        <div className="stockFormGroup">
                            <label>Modelo</label>
                            <input
                                value={form.modelo}
                                onChange={(e) => updateForm('modelo', e.target.value.toUpperCase())}
                                type="text"
                                maxLength={100}
                                placeholder="Ej. NOTE 15 PRO"
                            />
                            {errors.modelo && <small>{errors.modelo}</small>}
                        </div>

                        <div className="stockFormGroup">
                            <label>Código de producto</label>
                            <input
                                value={form.codigo}
                                onChange={(e) => updateForm('codigo', e.target.value.toUpperCase())}
                                type="text"
                                maxLength={100}
                                placeholder="Código interno"
                            />
                            {errors.codigo && <small>{errors.codigo}</small>}
                        </div>

                        <div className="stockFormGroup">
                            <label>RAM</label>
                            <Select
                                value={form.ram}
                                onChange={(value) => updateForm('ram', value)}
                                options={RAM_OPTIONS}
                                styles={selectStyles}
                                placeholder="RAM"
                            />
                            {errors.ram && <small>{errors.ram}</small>}
                        </div>

                        <div className="stockFormGroup">
                            <label>ROM</label>
                            <Select
                                value={form.rom}
                                onChange={(value) => updateForm('rom', value)}
                                options={ROM_OPTIONS}
                                styles={selectStyles}
                                placeholder="ROM"
                            />
                            {errors.rom && <small>{errors.rom}</small>}
                        </div>

                        <div className="stockFormGroup">
                            <label>Precio crédito</label>
                            <input
                                value={form.price}
                                onChange={(e) => updateForm('price', e.target.value.replace(/[^0-9.]/g, ''))}
                                type="text"
                                maxLength={8}
                                inputMode="decimal"
                                placeholder="Ej. 950"
                            />
                            {errors.price && <small>{errors.price}</small>}
                        </div>

                        {isEdit && canEdit && (
                            <div className="stockFormGroup stockStatusGroup">
                                <label>Estado del equipo</label>
                                <div className="stockStatusControl">
                                    <button
                                        type="button"
                                        className={form.estado === '1' ? 'active' : ''}
                                        onClick={() => updateForm('estado', '1')}
                                    >
                                        Activo
                                    </button>

                                    <button
                                        type="button"
                                        className={form.estado === '0' ? 'inactive active' : 'inactive'}
                                        onClick={() => updateForm('estado', '0')}
                                    >
                                        Inactivo
                                    </button>
                                </div>
                                <small className="stockHelpText">
                                    Si lo desactivas, solo el rol 1 podrá verlo en el stock.
                                </small>
                            </div>
                        )}

                        <div className="stockModalActions">
                            <button type="button" className="stockBtnSecondary" onClick={closeModals}>
                                Cancelar
                            </button>
                            <button type="submit" className="stockBtnPrimary">
                                Guardar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )
    }

    return (
        <div>
            {preload && <PreloadFetch />}
            <ScrollToTop />
            <Header user={user} namePage={props.namePage} />

            <section className="sectionBody sectionBodyCredits sectionBodyPhonesPro">
                <div className="containerCustom containerBody">
                    <div className="blockBody stockBlockBody">

                        <div className="stockHero">
                            <div>
                                {/* <span className="stockEyebrow">Catálogo MovilPro</span> */}
                                <h2>Listado de modelos</h2>
                                <p>Stock organizado por marca, filtros avanzados y vista profesional en tabla.</p>
                            </div>

                            {canAdd && (
                                <button type="button" className="stockAddButton" onClick={openAddModal}>
                                    AGREGAR STOCK
                                </button>
                            )}
                        </div>

                        <div className="stockSummaryGrid">
                            <div className="stockSummaryCard">
                                <FaBoxesStacked />
                                <span>Total equipos</span>
                                <strong>{summary.total}</strong>
                            </div>

                            <div className="stockSummaryCard">
                                <FaMobileScreenButton />
                                <span>Marcas visibles</span>
                                <strong>{summary.marcas}</strong>
                            </div>

                            <div className="stockSummaryCard">
                                <FaTag />
                                <span>Nuevos ingresos</span>
                                <strong>{summary.nuevos}</strong>
                            </div>

                            <div className="stockSummaryCard">
                                <FaTag />
                                <span>Bajaron precio</span>
                                <strong>{summary.bajaron}</strong>
                            </div>

                            {role === '1' && (
                                <div className="stockSummaryCard inactiveSummary">
                                    <FaTag />
                                    <span>Inactivos visibles</span>
                                    <strong>{summary.inactivos}</strong>
                                </div>
                            )}
                        </div>

                        <div className="stockFiltersPanel">
                            <div className="stockSearchBox">
                                <FaMagnifyingGlass />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Buscar por marca o modelo"
                                    type="text"
                                />
                            </div>

                            <div className="stockFiltersGrid">
                                <Select isMulti value={brandFilter} onChange={(value) => setBrandFilter(value || [])} options={BRAND_SELECT_OPTIONS} styles={selectStyles} placeholder="Filtrar por marca" />
                                <Select isMulti value={ramFilter} onChange={(value) => setRamFilter(value || [])} options={RAM_OPTIONS} styles={selectStyles} placeholder="Filtrar por RAM" />
                                <Select isMulti value={romFilter} onChange={(value) => setRomFilter(value || [])} options={ROM_OPTIONS} styles={selectStyles} placeholder="Filtrar por ROM" />
                                <Select value={orderFilter} onChange={setOrderFilter} options={ORDER_OPTIONS} styles={selectStyles} placeholder="Ordenar precio" />
                                <Select value={priceTypeFilter} onChange={setPriceTypeFilter} options={PRICE_TYPE_OPTIONS} styles={selectStyles} placeholder="Tipo de precio" />
                                <Select value={newEntryFilter} onChange={setNewEntryFilter} options={NEW_ENTRY_OPTIONS} styles={selectStyles} placeholder="Nuevos ingresos" />
                                {role === '1' && (
                                    <Select value={statusFilter} onChange={setStatusFilter} options={STATUS_FILTER_OPTIONS} styles={selectStyles} placeholder="Estado" />
                                )}
                            </div>

                            <div className="stockRangeGrid">
                                <label>
                                    Precio desde
                                    <input
                                        value={priceMin}
                                        onChange={(e) => setPriceMin(e.target.value.replace(/[^0-9.]/g, ''))}
                                        inputMode="decimal"
                                        placeholder="S/ 500"
                                    />
                                </label>

                                <label>
                                    Precio hasta
                                    <input
                                        value={priceMax}
                                        onChange={(e) => setPriceMax(e.target.value.replace(/[^0-9.]/g, ''))}
                                        inputMode="decimal"
                                        placeholder="S/ 800"
                                    />
                                </label>

                                <button type="button" onClick={resetFilters}>
                                    Limpiar filtros
                                </button>
                            </div>
                        </div>

                        <div className="stockLayoutToolbar">
                            <span>Vista de modelos</span>

                            <div className="stockLayoutActions">
                                <button
                                    type="button"
                                    className={layoutMode === 'table' ? 'active' : ''}
                                    onClick={() => setLayoutMode('table')}
                                >
                                    <FaTableCellsLarge /> Tabla amplia
                                </button>

                                <button
                                    type="button"
                                    className={layoutMode === 'compact' ? 'active' : ''}
                                    onClick={() => setLayoutMode('compact')}
                                >
                                    <FaList /> Tabla compacta
                                </button>
                            </div>
                        </div>

                        {canEdit && lectura !== 1 && (
                            <div className="stockBulkPanel">
                                <div className="stockBulkInfo">
                                    <span>Desactivación masiva</span>
                                    <strong>{selectedPhones.length} seleccionado(s)</strong>
                                    <p>Selecciona los modelos activos que ya no deseas mostrar en stock. Los inactivos seguirán visibles solo para rol 1.</p>
                                </div>

                                <div className="stockBulkActions">
                                    <button type="button" className="stockBtnSecondary" onClick={selectAllVisiblePhones} disabled={selectablePhones.length === 0}>
                                        Seleccionar activos visibles
                                    </button>
                                    <button type="button" className="stockBtnSecondary" onClick={clearSelectedPhones} disabled={selectedPhones.length === 0}>
                                        Limpiar selección
                                    </button>
                                    <button type="button" className="stockBulkDeactivateButton" onClick={bulkDeactivatePhones} disabled={selectedPhones.length === 0}>
                                        Desactivar seleccionados
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="stockBrandBlocks">
                            {groupedPhones.length === 0 && (
                                <div className="stockEmptyState">
                                    <h3>No se encontraron equipos</h3>
                                    <p>Ajusta los filtros o verifica el buscador.</p>
                                </div>
                            )}

                            {groupedPhones.map((group) => (
                                <div
                                    className="stockBrandBlock"
                                    key={group.brand}
                                    style={getBrandThemeStyle(group.brand)}
                                    data-brand={group.brand}
                                >
                                    <div className="stockBrandHeader">
                                        <div>
                                            <span>Marca</span>
                                            <h3>{group.brand}</h3>
                                        </div>

                                        <strong>{group.items.length} modelos</strong>
                                        <em className="stockBrandAccent" aria-hidden="true">
                                            {group.brand}
                                        </em>
                                    </div>

                                    <div className="stockTableWrapper">
                                        <table className={`stockTable ${layoutMode === 'compact' ? 'isCompactTable' : ''}`}>
                                            <thead>
                                                <tr>
                                                    {canEdit && lectura !== 1 && (
                                                        <th className="stockSelectColumn">
                                                            <input
                                                                type="checkbox"
                                                                checked={group.items.filter((phone) => String(phone.estado) === '1').length > 0 && group.items.filter((phone) => String(phone.estado) === '1').every((phone) => selectedPhonesSet.has(String(phone.linkNumber)))}
                                                                onChange={() => toggleGroupSelection(group.items)}
                                                                aria-label={`Seleccionar modelos activos de ${group.brand}`}
                                                            />
                                                        </th>
                                                    )}
                                                    <th>#</th>
                                                    <th>Modelo</th>
                                                    <th>RAM</th>
                                                    <th>ROM</th>
                                                    <th>Precio normal</th>
                                                    <th>Precio Crédito</th>
                                                    <th>Contado</th>
                                                    <th>Estado</th>
                                                    <th>Ingreso</th>
                                                    <th>Etiquetas</th>
                                                    {canEdit && lectura !== 1 && <th>Acción</th>}
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {group.items.map((phone, index) => {
                                                    const prices = getComputedPrices(phone)
                                                    const newFlag = isNewPhone(phone)
                                                    const dropFlag = isPriceDrop(phone)
                                                    const inactiveFlag = String(phone.estado) === '0'

                                                    return (
                                                        <tr
                                                            key={phone.linkNumber}
                                                            className={`
                                                                ${newFlag ? 'isNewRow' : ''}
                                                                ${dropFlag ? 'isDropRow' : ''}
                                                                ${inactiveFlag ? 'isInactiveRow' : ''}
                                                                ${selectedPhonesSet.has(String(phone.linkNumber)) ? 'isSelectedRow' : ''}
                                                            `}
                                                        >
                                                            {canEdit && lectura !== 1 && (
                                                                <td className="stockSelectColumn">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedPhonesSet.has(String(phone.linkNumber))}
                                                                        onChange={() => togglePhoneSelection(phone.linkNumber)}
                                                                        disabled={inactiveFlag}
                                                                        aria-label={`Seleccionar ${group.brand} ${phone.modelo}`}
                                                                    />
                                                                </td>
                                                            )}

                                                            <td className="stockTableIndex">
                                                                {index + 1}
                                                            </td>

                                                            <td className="stockTableModel">
                                                                <strong>{group.brand + ' ' + phone.modelo}</strong>
                                                                <span>{phone.code || '-'}</span>
                                                            </td>

                                                            <td>
                                                                <span className="stockTableBadge">{phone.ram}</span>
                                                            </td>

                                                            <td>
                                                                <span className="stockTableBadge">{phone.rom}</span>
                                                            </td>

                                                            <td className="stockTablePrice">
                                                                {money(prices.normalPrice)}
                                                            </td>

                                                            <td className="stockTablePrice stockTableMainPrice">
                                                                {money(prices.basePrice)}
                                                            </td>

                                                            <td className="stockTablePrice">
                                                                {money(prices.cashPrice)}
                                                            </td>

                                                            <td>
                                                                <span className={inactiveFlag ? 'stockStateBadge inactive' : 'stockStateBadge active'}>
                                                                    {inactiveFlag ? 'Inactivo' : 'Activo'}
                                                                </span>
                                                            </td>

                                                            <td>
                                                                {phone.fechaIngreso ? moment(phone.fechaIngreso).format('DD/MM/YYYY') : '-'}
                                                            </td>

                                                            <td>
                                                                <div className="stockTableTags">
                                                                    {newFlag && <span className="newBadge">Nuevo ingreso</span>}
                                                                    {dropFlag && <span className="dropBadge">Bajó precio</span>}
                                                                    {inactiveFlag && <span className="inactiveBadge">Inactivo</span>}
                                                                    {!newFlag && !dropFlag && !inactiveFlag && <span>-</span>}
                                                                </div>
                                                            </td>

                                                            {canEdit && lectura !== 1 && (
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="stockEditButton"
                                                                        onClick={() => openEditModal(phone)}
                                                                    >
                                                                        <ImPencil />
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <Footer />

            {showPopupAddStock && renderModal('add')}
            {showPopupEditStock && renderModal('edit')}
        </div>
    )
}

export default Phones
